import { Add } from "@mui/icons-material";
import { Box, Button, Typography } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import { useConfirm } from "material-ui-confirm";
import React, { useEffect, useState } from "react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import {
	closeEditWorkerSkillDialog,
	resetAddWorkerSkills,
	resetDeleteWorkerSkills,
	resetEditWorkerSkill,
} from "../../../../../export/workers";
import {
	CROSS_SKILLING_PERMISSIONS,
	SETTINGS_PERMISSIONS,
} from "../../../../config/permissions";
import SectionSearch from "../../../../design/section-search";
import { hasPermissionString } from "../../../../utils/helpers";
import {
	fetchTemplates,
} from "../../../evaluation-setup/store/evaluation-setup-slice";
import { fetchSkills } from "../../../skills/store/skills-slice";
import useWorkersData from "../../controllers/use-workers-data";
import AddWorkerSkillDialog from "./ui/dialog/add-worker-skill";
import EditWorkerSkillDialog from "./ui/dialog/edit-worker-skill-dialog";
import WorkerSkillsTable from "./ui/table/worker-skills-table";

const WorkerSkills = () => {
	const dispatch = useDispatch();
	const confirm = useConfirm();
	const { t } = useTranslation();
	const {
    deleteWorkerSkillStatus,
    deleteWorkerSkillError,
    addWorkerSkillStatus,
    addWorkerSkillError,
    editWorkerSkillStatus,
    editWorkerSkillError,
    isEditWorkerSkillDialogOpen,
    workerProfile,
    allSkillsWithPriority,
    fetchSkillsStatus,
    skillPriorityEnabled,
    skillExpiryEnabled,
    permissions,
    enableSkillEvaluationForm,
    allSkills,
  } = useWorkersData();

	const canEditWorker = hasPermissionString(
		permissions,
		SETTINGS_PERMISSIONS.WORKERS.EDIT
	);
	// GAT-7253 - adding a skill calls workers/skill, which checks this code.
	const canManageWorkerSkills = hasPermissionString(
		permissions,
		CROSS_SKILLING_PERMISSIONS.MANAGE_WORKER_SKILLS
	);
	const canEditSkillsWithEvaluation = hasPermissionString(
		permissions,
		SETTINGS_PERMISSIONS.WORKERS.EDIT_SKILLS_WITH_EVALUATION
	);

	const { userWorkCenters } = useSelector(
		(state: any) => ({
			userWorkCenters:
				state.manageWorkers.filterData.allWorkcenters?.map((wc: any) => String(wc.id)) ||
				[],
		})
	);

	const isInUserWorkcenter = useMemo(() => {
		const workerWorkCenterId = workerProfile?.workCenter?.id;
		if (!workerWorkCenterId) return true;
		return userWorkCenters.includes(String(workerWorkCenterId));
	}, [workerProfile?.workCenter?.id, userWorkCenters]);

	const handleisInUserWorkcenterAlert = useCallback(() => {
		confirm({
			title: "",
			description: t("workers_no-workcenter-access-skills"),
			confirmationText: t("common_close"),
			cancellationText: "",
		});
	}, [confirm, t]);

	const [searchText, setSearchText] = useState<string>("");

	const [isAddWorkerSkillDialogOpen, setIsAddWorkerSkillDialogOpen] =
		useState<boolean>(false);

	const [combinedSnackbar, setCombinedSnackbar] = useState<{
		open: boolean;
		message: React.ReactNode;
		severity: "success" | "error" | "info";
	}>({
		open: false,
		message: "",
		severity: "success",
	});

	useEffect(() => {
		if (combinedSnackbar.open) {
			dispatch(resetAddWorkerSkills());
		}
	}, [combinedSnackbar.open, dispatch]);

	useEffect(() => {
		if (enableSkillEvaluationForm && !canEditSkillsWithEvaluation) {
			dispatch(fetchTemplates() as any);
		}
	}, [enableSkillEvaluationForm, canEditSkillsWithEvaluation, dispatch]);

	useEffect(() => {
		if (skillPriorityEnabled && fetchSkillsStatus === "idle") {
			dispatch(fetchSkills() as any);
		}
	}, [dispatch, fetchSkillsStatus, skillPriorityEnabled]);

	const openAddWorkerSkillDialog = () => {
		dispatch(resetAddWorkerSkills());
		setIsAddWorkerSkillDialogOpen(true);
	};

	return (
		<Box
			sx={{
				borderRadius: "10px",
				border: "3px solid #f4f4f4",
			}}
			mb={5}
			mt={5}
		>
			<Box p={2}>
				<Box>
					<Typography fontWeight='bold'>
						{t("workers_qualified-skills")}
					</Typography>
					<Typography variant='caption'>
						{/* Select skills and skill level for each job role assigned. Add any
						additional skills. */}
					</Typography>
				</Box>
				<Box
					display={"flex"}
					alignItems={"center"}
					justifyContent={"space-between"}
					mt={2}
				>
					<SectionSearch
						value={searchText}
						setSearch={setSearchText}
						placeholder={t("common_search")}
					/>
					<Box>
						{canEditWorker && canManageWorkerSkills ? (
							<Button
								startIcon={<Add />}
								disabled={fetchSkillsStatus === "pending"}
								onClick={() => {
									if (isInUserWorkcenter) {
										openAddWorkerSkillDialog();
									} else {
										handleisInUserWorkcenterAlert();
									}
								}}
								sx={{
									opacity: isInUserWorkcenter && fetchSkillsStatus !== "pending" ? 1 : 0.5,
								}}
							>
								{t("workers_skills")}
							</Button>
						) : (
							<Box />
						)}
					</Box>
				</Box>
			</Box>
			<Box
				sx={{
					padding: "12px 8px",
				}}
			>
				<WorkerSkillsTable
					workerProfile={workerProfile}
					searchText={searchText}
					skillExpiryEnabled={skillExpiryEnabled}
					allSkillsWithPriority={allSkillsWithPriority}
          allSkills={allSkills}
					skillPriorityEnabled={skillPriorityEnabled}
				/>
			</Box>

			{isAddWorkerSkillDialogOpen && (
				<AddWorkerSkillDialog
					isOpen={isAddWorkerSkillDialogOpen}
					onClose={() => setIsAddWorkerSkillDialogOpen(false)}
					onCombinedResult={(result) => setCombinedSnackbar({ ...result, open: true })}
				/>
			)}

			{isEditWorkerSkillDialogOpen && (
				<EditWorkerSkillDialog
					open={isEditWorkerSkillDialogOpen}
					closeEditWorkerSkillDialog={closeEditWorkerSkillDialog}
					onCombinedResult={(result) => setCombinedSnackbar({ ...result, open: true })}
				/>
			)}

			{/* Add Skills for worker */}
			<Snackbar
				open={addWorkerSkillStatus === "fulfilled" && !isAddWorkerSkillDialogOpen && !combinedSnackbar.open}
				autoHideDuration={3000}
				onClose={() => dispatch(resetAddWorkerSkills())}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<MuiAlert elevation={6} variant='filled' severity='success'>
					{t("workers_skill-added-success")}
				</MuiAlert>
			</Snackbar>
			<Snackbar
				open={addWorkerSkillStatus === "error" && !isAddWorkerSkillDialogOpen && !combinedSnackbar.open}
				autoHideDuration={3000}
				onClose={() => dispatch(resetAddWorkerSkills())}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<MuiAlert elevation={6} variant='filled' severity='error'>
					{addWorkerSkillError ?? t("common_error-occurred")}
				</MuiAlert>
			</Snackbar>

			{/* Edit Skill for worker */}
			<Snackbar
				open={editWorkerSkillStatus === "fulfilled" && !combinedSnackbar.open}
				autoHideDuration={3000}
				onClose={() => dispatch(resetEditWorkerSkill())}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<MuiAlert elevation={6} variant='filled' severity='success'>
					{t("workers_skill-edited-success")}
				</MuiAlert>
			</Snackbar>
			<Snackbar
				open={editWorkerSkillStatus === "error" && !combinedSnackbar.open}
				autoHideDuration={3000}
				onClose={() => dispatch(resetEditWorkerSkill())}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<MuiAlert elevation={6} variant='filled' severity='error'>
					{editWorkerSkillError ?? t("common_error-occurred")}
				</MuiAlert>
			</Snackbar>

			{/* Delete Skills for worker */}
			<Snackbar
				open={deleteWorkerSkillStatus === "fulfilled"}
				autoHideDuration={3000}
				onClose={() => dispatch(resetDeleteWorkerSkills())}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<MuiAlert elevation={6} variant='filled' severity='success'>
					{t("workers_skill-removed-success")}
				</MuiAlert>
			</Snackbar>
			<Snackbar
				open={deleteWorkerSkillStatus === "error"}
				autoHideDuration={3000}
				onClose={() => dispatch(resetDeleteWorkerSkills())}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<MuiAlert elevation={6} variant='filled' severity='error'>
					{deleteWorkerSkillError ?? t("common_error-occurred")}
				</MuiAlert>
			</Snackbar>
			<Snackbar
				open={combinedSnackbar.open}
				autoHideDuration={2000}
				onClose={() => setCombinedSnackbar({ ...combinedSnackbar, open: false })}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<MuiAlert
					onClose={() => setCombinedSnackbar({ ...combinedSnackbar, open: false })}
					severity={combinedSnackbar.severity}
					variant="filled"
					sx={{ width: "100%" }}
				>
					{combinedSnackbar.message}
				</MuiAlert>
			</Snackbar>
		</Box>
	);
};

export default WorkerSkills;
